import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Alert, CircularProgress, Chip,
  FormControlLabel, Switch, Tooltip
} from '@mui/material';
import {
  Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon,
  ArrowBack as BackIcon, Event as EventIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import careerEventsService from '../../services/careerEventsService';

const ICON_OPTIONS = [
  'rate_review', 'trending_up', 'attach_money', 'school', 'work',
  'star', 'event', 'flag', 'warning', 'celebration', 'emoji_events',
  'military_tech', 'workspace_premium', 'verified', 'thumb_up'
];

const COLOR_OPTIONS = [
  '#2196F3', '#4CAF50', '#FF9800', '#F44336', '#9C27B0',
  '#00BCD4', '#795548', '#607D8B', '#E91E63', '#3F51B5'
];

const CareerEventTypesSettings = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    code: '', name: '', description: '', icon: 'event', color: '#2196F3',
    defaultFollowUpDescription: '', defaultFollowUpDays: '', sortOrder: 0, isActive: true
  });

  useEffect(() => { loadEventTypes(); }, []);

  const loadEventTypes = async () => {
    try {
      setLoading(true);
      const data = await careerEventsService.getEventTypes(true);
      setEventTypes(data || []);
    } catch (err) {
      setError('Failed to load event types');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData({
      code: '', name: '', description: '', icon: 'event', color: '#2196F3',
      defaultFollowUpDescription: '', defaultFollowUpDays: '', sortOrder: eventTypes.length + 1, isActive: true
    });
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '', name: item.name || '', description: item.description || '',
      icon: item.icon || 'event', color: item.color || '#2196F3',
      defaultFollowUpDescription: item.defaultFollowUpDescription || '',
      defaultFollowUpDays: item.defaultFollowUpDays || '', sortOrder: item.sortOrder || 0,
      isActive: item.isActive !== false
    });
    setDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await careerEventsService.deleteEventType(deleteConfirm.id);
      setSuccess('Event type deleted successfully');
      setDeleteConfirm(null);
      loadEventTypes();
    } catch (err) {
      const msg = err.response?.data || 'Failed to delete event type';
      setError(msg);
      setDeleteConfirm(null);
    }
  };

  const handleSave = async () => {
    if (!formData.code || !formData.name) {
      setError('Code and Name are required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...formData,
        defaultFollowUpDays: formData.defaultFollowUpDays ? parseInt(formData.defaultFollowUpDays) : null,
        sortOrder: parseInt(formData.sortOrder) || 0
      };
      if (editingItem) {
        await careerEventsService.updateEventType(editingItem.id, payload);
        setSuccess('Event type updated successfully');
      } else {
        await careerEventsService.createEventType(payload);
        setSuccess('Event type created successfully');
      }
      setDialogOpen(false);
      loadEventTypes();
    } catch (err) {
      setError(err.response?.data || 'Failed to save event type');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/settings')}><BackIcon /></IconButton>
        <EventIcon sx={{ fontSize: 32, color: 'primary.main' }} />
        <Typography variant="h5">Career Event Types</Typography>
      </Box>

      {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}

      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Event Types ({eventTypes.length})</Typography>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAdd}>Add Type</Button>
          </Box>

          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Code</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Icon</TableCell>
                  <TableCell>Color</TableCell>
                  <TableCell>Follow-up Default</TableCell>
                  <TableCell>Order</TableCell>
                  <TableCell>Active</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {eventTypes.map((item) => (
                  <TableRow key={item.id} hover sx={{ opacity: item.isActive ? 1 : 0.5 }}>
                    <TableCell><Typography variant="body2" fontFamily="monospace" fontWeight="bold">{item.code}</Typography></TableCell>
                    <TableCell><Typography variant="subtitle2">{item.name}</Typography></TableCell>
                    <TableCell><span className="material-icons" style={{ color: item.color }}>{item.icon}</span></TableCell>
                    <TableCell><Box sx={{ width: 24, height: 24, bgcolor: item.color, borderRadius: 1 }} /></TableCell>
                    <TableCell>{item.defaultFollowUpDays ? `${item.defaultFollowUpDays} days` : '-'}</TableCell>
                    <TableCell>{item.sortOrder}</TableCell>
                    <TableCell><Chip label={item.isActive ? 'Active' : 'Inactive'} size="small" color={item.isActive ? 'success' : 'default'} /></TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => handleEdit(item)}><EditIcon /></IconButton></Tooltip>
                      <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => setDeleteConfirm(item)}><DeleteIcon /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Event Type' : 'Add Event Type'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Code" value={formData.code} onChange={(e) => handleChange('code', e.target.value.toUpperCase())}
                required fullWidth disabled={!!editingItem} inputProps={{ maxLength: 50 }} />
              <TextField label="Sort Order" type="number" value={formData.sortOrder}
                onChange={(e) => handleChange('sortOrder', e.target.value)} sx={{ width: 120 }} />
            </Box>
            <TextField label="Name" value={formData.name} onChange={(e) => handleChange('name', e.target.value)} required fullWidth />
            <TextField label="Description" value={formData.description} onChange={(e) => handleChange('description', e.target.value)}
              fullWidth multiline rows={2} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Icon</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {ICON_OPTIONS.map((icon) => (
                    <IconButton key={icon} size="small" onClick={() => handleChange('icon', icon)}
                      sx={{ border: formData.icon === icon ? '2px solid' : '1px solid #ddd', borderColor: formData.icon === icon ? 'primary.main' : '#ddd' }}>
                      <span className="material-icons" style={{ fontSize: 20 }}>{icon}</span>
                    </IconButton>
                  ))}
                </Box>
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="caption" color="text.secondary">Color</Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                  {COLOR_OPTIONS.map((color) => (
                    <IconButton key={color} size="small" onClick={() => handleChange('color', color)}
                      sx={{ width: 28, height: 28, bgcolor: color, border: formData.color === color ? '3px solid #000' : 'none', '&:hover': { bgcolor: color } }} />
                  ))}
                </Box>
              </Box>
            </Box>
            <Typography variant="subtitle2" sx={{ mt: 1 }}>Default Follow-up Settings</Typography>
            <TextField label="Default Follow-up Description" value={formData.defaultFollowUpDescription}
              onChange={(e) => handleChange('defaultFollowUpDescription', e.target.value)} fullWidth multiline rows={2}
              helperText="This description will be pre-filled when creating a follow-up for this event type" />
            <TextField label="Default Follow-up Days" type="number" value={formData.defaultFollowUpDays}
              onChange={(e) => handleChange('defaultFollowUpDays', e.target.value)} sx={{ width: 200 }}
              helperText="Days after event for follow-up due date" />
            {editingItem && (
              <FormControlLabel control={<Switch checked={formData.isActive} onChange={(e) => handleChange('isActive', e.target.checked)} />}
                label="Active" />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving}>
            {saving ? <CircularProgress size={20} /> : (editingItem ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <DialogTitle>Delete Event Type</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>?</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will deactivate the event type. If it's used in existing events, deletion will be prevented.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirm(null)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CareerEventTypesSettings;

